/// Os roteiros que guardam progresso e seus passos (ADR 0084). A API só aceita o que está aqui; o
/// texto de cada passo mora na interface.
export const guideCatalog = Object.freeze({
  'month-closing': Object.freeze([
    'sales',
    'statement',
    'operator-report',
    'expenses',
    'purchases',
    'commissions',
    'reconciliation',
    'classification',
    'cash-closing',
    'reports-review',
    'accounting-closing',
    'send-reports',
  ]),
});

export type GuideKey = keyof typeof guideCatalog;

export const GUIDE_MARK_STATUSES = ['CONFIRMED', 'NOT_APPLICABLE'] as const;
export type GuideMarkStatus = (typeof GUIDE_MARK_STATUSES)[number];

export function isGuideStep(guideKey: string, stepKey: string): boolean {
  const steps = (guideCatalog as Readonly<Record<string, readonly string[]>>)[guideKey];
  return Array.isArray(steps) && steps.includes(stepKey);
}

export interface GuideCapabilities {
  moduleKeys: readonly string[];
  featureKeys: readonly string[];
}

const stepModules: Readonly<Record<string, string>> = {
  sales: 'sales',
  purchases: 'purchasing',
  classification: 'accounting',
  'reports-review': 'accounting',
  'accounting-closing': 'accounting',
  'send-reports': 'accounting',
  'fix-closed-month': 'accounting',
};

/// A mesma contratação governa o menu, os passos e as rotas de progresso.
export function guideIsContracted(guideKey: string, capabilities: GuideCapabilities): boolean {
  return guideKey === 'month-closing' && capabilities.moduleKeys.includes('finance');
}

export function guideStepIsContracted(
  guideKey: string,
  stepKey: string,
  capabilities: GuideCapabilities,
): boolean {
  if (!guideIsContracted(guideKey, capabilities)) return false;
  if (!isGuideStep(guideKey, stepKey) && stepKey !== 'fix-closed-month') return false;
  if (stepKey === 'commissions') return capabilities.featureKeys.includes('sales.commissions');
  const moduleKey = stepModules[stepKey];
  return !moduleKey || capabilities.moduleKeys.includes(moduleKey);
}

/// Passos automáticos nunca recebem uma confirmação humana, inclusive pela API.
export function guideStepAcceptsConfirmation(guideKey: string, stepKey: string): boolean {
  return (
    isGuideStep(guideKey, stepKey) &&
    ![
      'commissions',
      'reconciliation',
      'classification',
      'cash-closing',
      'accounting-closing',
    ].includes(stepKey)
  );
}

const stepTitles: Readonly<Record<string, string>> = {
  sales: 'Conferir as vendas do mês',
  statement: 'Importar o extrato de cada conta financeira',
  'operator-report': 'Importar o relatório da operadora de cartão',
  expenses: 'Lançar as despesas do mês',
  purchases: 'Registrar as compras recebidas',
  commissions: 'Conferir e apurar as comissões',
  reconciliation: 'Conciliar o extrato',
  classification: 'Classificar os lançamentos',
  'cash-closing': 'Fechar o caixa',
  'reports-review': 'Conferir o DRE e o Fluxo de caixa',
  'accounting-closing': 'Fazer o Fechamento contábil',
  'send-reports': 'Enviar aos sócios ou ao contador',
  'fix-closed-month': 'Corrigir um mês já fechado',
};

export function guideStepTitle(guideKey: string, stepKey: string): string {
  return guideKey === 'month-closing' && Object.hasOwn(stepTitles, stepKey)
    ? stepTitles[stepKey]!
    : 'Passo do roteiro';
}
